import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Play, Square, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

interface CuttingTimerProps {
  startedAt?: Date | string | null;
  onStart?: () => Promise<void>;
  onStop?: () => Promise<void>;
  materialName?: string;
  compact?: boolean;
  disabled?: boolean;
}

export function CuttingTimer({
  startedAt,
  onStart,
  onStop,
  materialName,
  compact = false,
  disabled = false,
}: CuttingTimerProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(false);

  const startDate = startedAt ? new Date(startedAt) : null;

  useEffect(() => {
    if (!startDate) {
      setIsRunning(false);
      setElapsedSeconds(0);
      return;
    }

    setIsRunning(true);
    const updateElapsed = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startDate.getTime()) / 1000);
      setElapsedSeconds(Math.max(0, elapsed));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startDate?.getTime()]);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleStart = async () => {
    if (!onStart) return;
    setLoading(true);
    try {
      await onStart();
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!onStop) return;
    setLoading(true);
    try {
      await onStop();
    } finally {
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {isRunning ? (
          <>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 font-mono">
              <Clock className="w-3 h-3 mr-1 animate-pulse" />
              {formatTime(elapsedSeconds)}
            </Badge>
            {onStop && (
              <Button size="sm" variant="destructive" onClick={handleStop} disabled={loading || disabled}>
                <Square className="w-3 h-3" />
              </Button>
            )}
          </>
        ) : (
          onStart && (
            <Button size="sm" variant="outline" onClick={handleStart} disabled={loading || disabled}>
              <Play className="w-3 h-3 mr-1" />
              Iniciar
            </Button>
          )
        )}
      </div>
    );
  }

  return (
    <Card className={cn("p-4", isRunning && "border-green-500 bg-green-50/50 dark:bg-green-950/20")}>
      {materialName && (
        <p className="text-sm text-muted-foreground mb-2">{materialName}</p>
      )}
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock className={cn("w-5 h-5", isRunning ? "text-green-600 animate-pulse" : "text-muted-foreground")} />
          <span className={cn("font-mono text-2xl font-bold", isRunning ? "text-green-700" : "text-muted-foreground")}>
            {formatTime(elapsedSeconds)}
          </span>
        </div>
        
        {isRunning ? (
          onStop && (
            <Button onClick={handleStop} disabled={loading || disabled} variant="destructive">
              <Square className="w-4 h-4 mr-2" />
              Finalizar Corte
            </Button>
          )
        ) : (
          onStart && (
            <Button onClick={handleStart} disabled={loading || disabled} className="bg-green-600 hover:bg-green-700">
              <Play className="w-4 h-4 mr-2" />
              Iniciar Corte
            </Button>
          )
        )}
      </div>
    </Card>
  );
}
